import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  InputGroup,
  Table,
  Spinner,
  Pagination,
} from "react-bootstrap";
import {
  Download,
  RefreshCcw,
  Search,
  ChevronUp,
  ChevronDown,
} from "react-feather";

import * as XLSX from "xlsx";

import { fetchWardsThunk } from "../../../store/slices/wardsSlice";
import { fetchMeterTypesThunk } from "../../../store/slices/meterTypesSlice"; // optional
import {
  fetchHouseSummaryReportThunk,
  clearHouseSummaryReport,
} from "../../../store/slices/houseSummaryReportSlice";

// ✅ Kannada font (optional but recommended)

import { printTableReport } from "./reportPrintUtils";

import "./ReportPage.css";

const HouseSummaryReportPage = () => {
  const dispatch = useDispatch();

  const wards = useSelector((s) => s.wards?.list || []);
  const meterTypes = useSelector((s) => s.meterTypes?.list || []);

  const { rows = [], totals, loading, error } = useSelector(
    (s) => s.houseSummaryReport || {}
  );

  const [wardId, setWardId] = useState("");
  const [mtrType, setMtrType] = useState("");
  const [onlyWaterYes, setOnlyWaterYes] = useState("1");
  const [dueFilter, setDueFilter] = useState("water_gt_0");
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    dispatch(fetchWardsThunk());
    dispatch(fetchMeterTypesThunk()); // remove if you didn't create thunk
  }, [dispatch]);

  const handleSort = (field) => {
    setSortField((currentField) => {
      if (currentField === field) {
        setSortDirection((currentDirection) =>
          currentDirection === "asc" ? "desc" : "asc"
        );

        return currentField;
      }

      setSortDirection("asc");
      return field;
    });

    setPage(1);
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) {
      return (
        <span className="ms-1 report-sort-default-icon">
          <ChevronUp size={11} />
          <ChevronDown size={11} />
        </span>
      );
    }

    return sortDirection === "asc" ? (
      <ChevronUp size={14} className="ms-1" />
    ) : (
      <ChevronDown size={14} className="ms-1" />
    );
  };

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();

    const searchedRows = !q
      ? [...rows]
      : rows.filter((r) => {
          const searchableText = `
            ${r.home_id}
            ${r.qr_code}
            ${r.door_no}
            ${r.family_head}
            ${r.mobile}
            ${r.ward_name}
            ${r.mtr_type}
            ${r.water_balance}
            ${r.slrm_bal}
            ${r.balance}
            ${r.status}
          `.toLowerCase();

          return searchableText.includes(q);
        });

    if (!sortField) {
      return searchedRows;
    }

    const numericFields = [
      "home_id",
      "mobile",
      "water_balance",
      "slrm_bal",
      "balance",
    ];

    return [...searchedRows].sort((a, b) => {
      let aValue = a?.[sortField];
      let bValue = b?.[sortField];

      if (numericFields.includes(sortField)) {
        aValue = Number(aValue || 0);
        bValue = Number(bValue || 0);

        return sortDirection === "asc"
          ? aValue - bValue
          : bValue - aValue;
      }

      const comparison = String(aValue || "").localeCompare(
        String(bValue || ""),
        undefined,
        {
          numeric: true,
          sensitivity: "base",
        }
      );

      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [rows, search, sortField, sortDirection]);

  useEffect(() => {
    setPage(1);
  }, [wardId, mtrType, onlyWaterYes, dueFilter, search, rows.length]);

  const total = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);

  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPage, pageSize]);

  const handleLoad = () => {
    dispatch(
      fetchHouseSummaryReportThunk({
        ward_id: wardId,
        mtr_type: mtrType,
        only_water_yes: onlyWaterYes,
        due_filter: dueFilter,
      })
    );
  };

  const handleClear = () => {
    setWardId("");
    setMtrType("");
    setOnlyWaterYes("1");
    setDueFilter("water_gt_0");
    setSearch("");
    setSortField("");
    setSortDirection("asc");
    setPage(1);
    dispatch(clearHouseSummaryReport());
  };

  const exportExcel = () => {
    const data = filteredRows.map((r, idx) => ({
      "#": idx + 1,
      "House No": r.qr_code,
      "Door No": r.door_no,
      Name: r.family_head,
      Mobile: r.mobile,
      Ward: r.ward_name,
      "Meter Type": r.mtr_type,
      "Water Due": r.water_balance,
      "SLRM Due": r.slrm_bal,
      "Total Due": r.balance,
      Status: r.status,
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "House Summary");
    XLSX.writeFile(wb, "house_summary_report.xlsx");
  };

  // ✅ Kannada font setup (safe)

  const exportPDF = () => {
    const data = filteredRows.map((r, idx) => ({
      "#": idx + 1,
      "House No": r.qr_code ?? "",
      "Door No": r.door_no ?? "",
      Name: r.family_head ?? "",
      Mobile: r.mobile ?? "",
      Ward: r.ward_name ?? "",
      "Meter Type": r.mtr_type ?? "",
      "Water Due": r.water_balance ?? "",
      "SLRM Due": r.slrm_bal ?? "",
      "Total Due": r.balance ?? "",
      Status: r.status ?? "",
    }));

    printTableReport({
      title: "House Summary - Report",
fileName: "house_summary_report.pdf",
      data,
      metaLines: [`Total Records: ${filteredRows.length}`],
    });
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const items = [];
    const maxButtons = 5;
    let start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let end = Math.min(totalPages, start + maxButtons - 1);
    start = Math.max(1, end - maxButtons + 1);

    items.push(
      <Pagination.Prev
        key="prev"
        disabled={currentPage === 1}
        onClick={() => setPage((p) => Math.max(1, p - 1))}
      />
    );

    for (let p = start; p <= end; p++) {
      items.push(
        <Pagination.Item
          key={p}
          active={p === currentPage}
          onClick={() => setPage(p)}
        >
          {p}
        </Pagination.Item>
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        disabled={currentPage === totalPages}
        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
      />
    );

    return <Pagination className="mb-0">{items}</Pagination>;
  };

  return (
    <div className="report-page">
      <Card className="report-card">
        <Card.Header className="report-card-header">
          <div className="report-header-row">
            <div>
              <div className="report-title">House Summary - Report</div>
              {/* <div className="report-subtitle">
                Filter ward/meter type + due filter, load, then export Excel/PDF
              </div> */}
            </div>
          </div>
        </Card.Header>

        <Card.Body>
          {error ? <div className="alert alert-danger mb-3">{error}</div> : null}

          {/* TOP TOTALS */}
          {totals ? (
            <Row className="g-3 mb-3">
              <Col md={3}>
                <Card className="p-3">
                  <div className="text-muted">Total Houses</div>
                  <div className="h5 mb-0">
                    {Number(totals.total_houses || 0).toLocaleString()}
                  </div>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="p-3">
                  <div className="text-muted">Total Water Due</div>
                  <div className="h5 mb-0">
                    {Number(totals.total_water_balance || 0).toLocaleString()}
                  </div>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="p-3">
                  <div className="text-muted">Total SLRM Due</div>
                  <div className="h5 mb-0">
                    {Number(totals.total_slrm_balance || 0).toLocaleString()}
                  </div>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="p-3">
                  <div className="text-muted">Total Due</div>
                  <div className="h5 mb-0">
                    {Number(totals.total_balance || 0).toLocaleString()}
                  </div>
                </Card>
              </Col>
            </Row>
          ) : null}

          {/* FILTERS */}
          <div className="report-filter-box">
            <Row className="g-3">
              <Col lg={3} md={6}>
                <Form.Label className="report-label">Ward</Form.Label>
                <Form.Select
                  value={wardId}
                  onChange={(e) => setWardId(e.target.value)}
                >
                  <option value="">All Wards</option>
                  {wards.map((w) => (
                    <option key={w.ward_id} value={String(w.ward_id)}>
                      {w.ward_name}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col lg={3} md={6}>
                <Form.Label className="report-label">Meter Type</Form.Label>
                <Form.Select
                  value={mtrType}
                  onChange={(e) => setMtrType(e.target.value)}
                >
                  <option value="">All Types</option>
                  {meterTypes.map((m) => (
                    <option key={m.wbs_id} value={String(m.wbs_id)}>
                      {m.mtr_type}
                    </option>
                  ))}
                </Form.Select>
              </Col>

            <Col lg={3} md={6}>
  <Form.Label className="report-label">Only Water Houses</Form.Label>
  <Form.Select
    value={onlyWaterYes}
    onChange={(e) => setOnlyWaterYes(e.target.value)}
  >
    <option value="">All</option>
    <option value="1">Yes (is_water = Yes)</option>
    <option value="0">No (is_water = No)</option>
  </Form.Select>
</Col>
              <Col lg={3} md={6}>
                <Form.Label className="report-label">Due Filter</Form.Label>
                <Form.Select
                  value={dueFilter}
                  onChange={(e) => setDueFilter(e.target.value)}
                >
                  <option value="">No Filter</option>
                  <option value="water_gt_0">Water Due &gt; 0</option>
                  <option value="water_eq_0">Water Due = 0</option>
                  <option value="slrm_gt_0">SLRM Due &gt; 0</option>
                  <option value="total_gt_0">Total Due &gt; 0</option>
                </Form.Select>
              </Col>

              <Col lg={12} className="d-flex align-items-end">
                <div className="report-filter-actions">
                  <Button
                    className="report-btn-primary"
                    variant="primary"
                    onClick={handleLoad}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Spinner size="sm" className="me-2" /> Loading
                      </>
                    ) : (
                      "Load"
                    )}
                  </Button>

                  <Button
                    className="report-btn"
                    variant="outline-secondary"
                    onClick={handleClear}
                  >
                    <RefreshCcw size={16} className="me-2" />
                    Clear
                  </Button>
                </div>
              </Col>
            </Row>
          </div>

          {/* TOOLBAR */}
          <div className="report-table-toolbar">
            <div className="report-table-title">
              Houses{" "}
              <span className="report-count">
                ({total.toLocaleString()} records)
              </span>
            </div>

            <div className="report-table-right">
              <InputGroup className="report-search">
                <InputGroup.Text>
                  <Search size={16} />
                </InputGroup.Text>
                <Form.Control
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search house / name / ward..."
                />
              </InputGroup>

              <Button
                className="report-btn"
                variant="outline-primary"
                onClick={exportExcel}
                disabled={!filteredRows.length}
              >
                <Download size={16} className="me-2" />
                Excel
              </Button>

              <Button
                className="report-btn"
                variant="outline-danger"
                onClick={exportPDF}
                disabled={!filteredRows.length}
              >
                <Download size={16} className="me-2" />
                PDF
              </Button>
            </div>
          </div>

          {/* TABLE */}
          <div className="report-table-wrap">
            <div className="report-table-scroll">
              <Table hover className="mb-0 report-table">
                <thead>
                  <tr>
                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "home_id" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("home_id")}
                      >
                        # {renderSortIcon("home_id")}
                      </button>
                    </th>

                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "qr_code" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("qr_code")}
                      >
                        House No {renderSortIcon("qr_code")}
                      </button>
                    </th>

                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "door_no" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("door_no")}
                      >
                        Door No {renderSortIcon("door_no")}
                      </button>
                    </th>

                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "family_head" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("family_head")}
                      >
                        Name {renderSortIcon("family_head")}
                      </button>
                    </th>

                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "mobile" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("mobile")}
                      >
                        Mobile {renderSortIcon("mobile")}
                      </button>
                    </th>

                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "ward_name" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("ward_name")}
                      >
                        Ward {renderSortIcon("ward_name")}
                      </button>
                    </th>

                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "mtr_type" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("mtr_type")}
                      >
                        Meter Type {renderSortIcon("mtr_type")}
                      </button>
                    </th>

                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "water_balance" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("water_balance")}
                      >
                        Water Due {renderSortIcon("water_balance")}
                      </button>
                    </th>

                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "slrm_bal" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("slrm_bal")}
                      >
                        SLRM Due {renderSortIcon("slrm_bal")}
                      </button>
                    </th>

                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "balance" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("balance")}
                      >
                        Total Due {renderSortIcon("balance")}
                      </button>
                    </th>

                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "status" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("status")}
                      >
                        Status {renderSortIcon("status")}
                      </button>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="11" className="text-center py-4">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Loading...
                      </td>
                    </tr>
                  ) : pagedRows.length ? (
                    pagedRows.map((r, idx) => (
                      <tr key={String(r.home_id)}>
                        <td>{(currentPage - 1) * pageSize + idx + 1}</td>
                        <td>{r.qr_code}</td>
                        <td>{r.door_no}</td>
                        <td>{r.family_head}</td>
                        <td>{r.mobile}</td>
                        <td>{r.ward_name}</td>
                        <td>{r.mtr_type}</td>
                        <td>{r.water_balance}</td>
                        <td>{r.slrm_bal}</td>
                        <td>{r.balance}</td>
                        <td>{r.status}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="11" className="text-center py-4">
                        No data found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            <div className="report-pagination-bar">
              <div className="report-page-size">
                <span>Rows:</span>
                <Form.Select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="page-size-select"
                >
                  {[10, 20, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </Form.Select>
              </div>

              <div className="report-pagination">{renderPagination()}</div>

              <div className="report-page-info">
                Page {currentPage} / {totalPages}
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default HouseSummaryReportPage;
